import {
  buildCursorPath,
  catalogBySlug,
  LogSet,
  paginate,
  similarTodayAnchor,
  TODAY_ANCHOR_WEEKS,
  todayHistoryFromSets,
  WEEK_MS,
  type Exercise,
  type LogSet as LogSetT,
  type TodayAnchor,
  type TodayAnchorSet,
  type TodayHistory,
} from '@heraion/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import { FETCH_CONCURRENCY, mapBounded } from './lastLoggedBeyondWindow';
import { loadExerciseSets } from './useExerciseSetsFallback';

// P0 57d74690: each candidate's history for «Build today's session». One
// read of every set in the anchor window, then the indexed door only for
// what that window does not hold.

const SETS_PAGE_SIZE = 500;
const SETS_MAX_PAGES = 10;
const WINDOW_MS = TODAY_ANCHOR_WEEKS * WEEK_MS;

/** Every set since `sinceMs`, all exercises. Null: the read failed. */
async function setsSince(apiFetch: ProgramApiFetch, sinceMs: number): Promise<LogSetT[] | null> {
  const query = `since=${encodeURIComponent(new Date(sinceMs).toISOString())}&limit=${SETS_PAGE_SIZE}`;
  try {
    const { items } = await paginate<unknown, string>(
      async (cursor) => {
        const res = await apiFetch<{ items?: unknown[]; cursor?: string | null }>(
          buildCursorPath('/api/sets', query, cursor),
        );
        return { items: res.items ?? [], cursor: res.cursor ?? null };
      },
      { maxPages: SETS_MAX_PAGES },
    );
    return items.flatMap((raw) => {
      const parsed = LogSet.safeParse(raw);
      return parsed.success ? [parsed.data] : [];
    });
  } catch {
    return null;
  }
}

// A drop-in set has no session: its day stands in, so practice counts days.
const fromLogSet = (l: LogSetT): TodayAnchorSet => {
  const { kind, performedAt, sessionId, ...rest } = l;
  return {
    ...rest,
    type: kind,
    atMs: Date.parse(performedAt),
    session: sessionId ?? `day:${performedAt.slice(0, 10)}`,
  };
};

const push = (by: Map<string, TodayAnchorSet[]>, slug: string, set: TodayAnchorSet) => {
  const list = by.get(slug);
  if (list) list.push(set);
  else by.set(slug, [set]);
};

export async function loadTodayHistory(opts: {
  slugs: readonly string[];
  catalog: readonly Exercise[];
  apiFetch: ProgramApiFetch;
  nowMs: number;
}): Promise<Map<string, TodayHistory>> {
  const { slugs, catalog, apiFetch, nowMs } = opts;
  const sets = new Map<string, TodayAnchorSet[]>();
  for (const l of (await setsSince(apiFetch, nowMs - WINDOW_MS)) ?? []) {
    push(sets, l.exerciseSlug, fromLogSet(l));
  }
  // No set in the window, or the read failed: the indexed door, for the last
  // session at any age (D1). A cache younger than the window cannot miss a
  // set older than it, so that is its age limit.
  const rest = slugs.filter((slug) => !sets.has(slug));
  const older = await mapBounded(rest, FETCH_CONCURRENCY, (slug) =>
    loadExerciseSets(slug, apiFetch, { maxAgeMs: WINDOW_MS }),
  );
  rest.forEach((slug, i) => {
    for (const l of older[i] ?? []) push(sets, slug, fromLogSet(l));
  });

  const own = new Map<string, TodayHistory>();
  for (const [slug, list] of sets) own.set(slug, todayHistoryFromSets(slug, list, nowMs));
  const anchors = new Map<string, TodayAnchor | null>(
    [...own].map(([slug, h]) => [slug, h.anchor]),
  );
  const index = catalogBySlug(catalog);
  const out = new Map<string, TodayHistory>();
  for (const slug of slugs) {
    const h = own.get(slug) ?? { anchor: null, practice: 0 };
    const ex = index.get(slug);
    out.set(
      slug,
      h.anchor || !ex ? h : { ...h, anchor: similarTodayAnchor(ex, catalog, anchors) },
    );
  }
  return out;
}
